import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Input, Optional, Self, ViewChild } from '@angular/core';
import { FormControlDirective, NgControl } from '@angular/forms';
import { trigger } from '@angular/animations';
import { CanColorCtor, CanDisableCtor, mixinColor, mixinDisabled } from '@angular/material/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { randomId } from '@trilean/n7';
import { ControlValueAccessorProxy } from '@trilean/tr3';
import { fadeInOut } from '@trilean/tr3/animations';

// Boilerplate for applying mixins.
class ComponentBase extends ControlValueAccessorProxy {
  constructor(ngControl: NgControl, public _elementRef: ElementRef) {
    super(ngControl);
  }
}

const _ComponentMixinBase: CanColorCtor & CanDisableCtor & typeof ComponentBase = mixinColor(
  mixinDisabled(ComponentBase),
);

export interface PasswordEditErrorMessages {
  required?: string;
  requiredLength?: string;
  requiredUniqueChars?: string;
  requireNonAlphanumeric?: string;
  requireLowercase?: string;
  requireUppercase?: string;
  requireDigit?: string;
}

@Component({
  selector: 'tr3-password-edit',
  templateUrl: './password-edit.component.html',
  styleUrls: ['./password-edit.component.scss'],
  inputs: ['disabled', 'color'],
  animations: [trigger('fadeInOut', fadeInOut())],
})
export class PasswordEditComponent extends _ComponentMixinBase {
  @Input() id: string = randomId('password-edit');
  @Input() label?: string;
  @Input() hintLabel?: string;
  @Input() appearance: MatFormFieldAppearance = 'standard';
  @Input() errorMessages?: PasswordEditErrorMessages;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }
  private _required = false;
  static ngAcceptInputType_required: BooleanInput;

  @Input()
  get revealed(): boolean {
    return this._revealed;
  }
  set revealed(value: boolean) {
    this._revealed = coerceBooleanProperty(value);
  }
  private _revealed = false;
  static ngAcceptInputType_revealed: BooleanInput;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get value(): any {
    return this.formControl?.value;
  }

  @ViewChild(FormControlDirective)
  set formControlDirective(fcd: FormControlDirective | undefined) {
    super.formControlDirective = fcd;
  }

  constructor(@Optional() @Self() ngControl: NgControl, elementRef: ElementRef) {
    super(ngControl, elementRef);
  }

  hasError(error: keyof PasswordEditErrorMessages): boolean {
    return !!this.errorMessages?.[error] && !!this.formControl?.hasError(error);
  }

  fmtError(error: keyof PasswordEditErrorMessages): string | undefined {
    return this.errorMessages?.[error]?.replace('{{expected}}', this.formControl?.errors?.[error].expected);
  }
}
